import * as pc from "playcanvas";
import { MATCH_FORMAT } from "@hupper/shared";
import type { GameClient, MatchHudState, RemotePlayerState } from "../net/GameClient";
import type { FirstPersonController } from "../player/FirstPersonController";
import type { CharacterFactory } from "../player/CharacterFactory";
import type { HalftimePose } from "../player/RemoteCharacter";
import { RemotePlayer } from "../player/RemotePlayer";
import { HalftimeOverlay } from "../ui/HalftimeOverlay";

/**
 * HalftimeCinematic — the one-time side-swap cinematic.
 *
 * When the server enters the `halftime` phase (the sides just swapped, right
 * before the second half's first buy), this stages the LOCAL player's
 * mannequin on the mid platform in its NEW team colours, playing one of the
 * halftime poses at random (arm stretch / stop-walking-with-rifle), while a
 * dedicated camera sweeps around it and the {@link HalftimeOverlay} reveals
 * the new faction title + countdown. Everything tears down when the phase
 * leaves `halftime` and the buy phase begins (which also hard-snaps the
 * local player to its new-side spawn — see GameClient's live-round hook).
 */

const CT_ACCENT = new pc.Color(0.32, 0.45, 0.72); // blue
const T_ACCENT = new pc.Color(0.74, 0.42, 0.22); // orange
const SKY = new pc.Color(0.52, 0.72, 0.90); // matches the gameplay camera clear

/** Platform top (the 12×12 mid block spans y 0..1.5). */
const STAGE_Y = 1.5;

/** Gameplay HUD layers hidden while the cinematic is up — same set as the
 *  VS intro; they'd otherwise bleed through the transparent overlay. */
const HUD_HIDE_CSS =
  "body.halftime-active #crosshair," +
  "body.halftime-active #scoreboard," +
  "body.halftime-active #hud-center," +
  "body.halftime-active #weapon-inv," +
  "body.halftime-active #kill-feed{display:none!important;}";

export class HalftimeCinematic {
  private readonly app: pc.AppBase;
  private readonly client: GameClient | null;
  private readonly player: FirstPersonController;
  private readonly factory: CharacterFactory;
  private readonly overlay = new HalftimeOverlay();

  private active = false;
  private lastPhase: string | null = null;
  private fighter: RemotePlayer | null = null;
  private camera: pc.Entity | null = null;
  private elapsed = 0;
  private pose: HalftimePose = "stretch";
  /** Smoothed camera pivot — trails the mannequin's hips so clip root
   *  motion (the walk-stop backpedal) keeps the body centred in frame. */
  private readonly focus = new pc.Vec3();
  private readonly hipsTmp = new pc.Vec3();
  /** True while the `?halftime=` preview loop drives the cinematic — live
   *  HUD phases must not tear it down (a local dev game-server's warmup
   *  snapshots would otherwise end it the moment it begins). */
  private debugLoop = false;

  constructor(
    app: pc.AppBase,
    client: GameClient | null,
    player: FirstPersonController,
    factory: CharacterFactory,
  ) {
    this.app = app;
    this.client = client;
    this.player = player;
    this.factory = factory;
    this.app.on("update", (dt: number) => this.update(dt));
    this.client?.onMatchHud((hud) => this.onHud(hud));

    const style = document.createElement("style");
    style.textContent = HUD_HIDE_CSS;
    document.head.appendChild(style);
  }

  private onHud(hud: MatchHudState): void {
    if (this.debugLoop) return;
    if (hud.phase === "halftime" && this.lastPhase !== "halftime") this.begin();
    else if (hud.phase !== "halftime" && this.active) this.end();
    if (this.active && typeof hud.halftimeSecondsLeft === "number") {
      this.overlay.setCountdown(hud.halftimeSecondsLeft);
    }
    this.lastPhase = hud.phase;
  }

  private begin(override?: { team: "ct" | "t"; pose?: HalftimePose }): void {
    if (this.active) return;
    this.active = true;
    this.elapsed = 0;

    // The swap already ran server-side before the phase flipped, so the
    // local snapshot carries the NEW team — that's the side to stage.
    const team: "ct" | "t" =
      override?.team ?? (this.client?.getLatestSelf()?.team === "t" ? "t" : "ct");
    const pose: HalftimePose =
      override?.pose ?? (Math.random() < 0.5 ? "stretch" : "rifle_walk_stop");
    this.pose = pose;
    // Start the pivot at the standing hips height on the stage mark; update()
    // trails it onto the real hips once the skinned character is up.
    this.focus.set(0, STAGE_Y + 1.0, 0);

    this.overlay.show(team);
    document.body.classList.add("halftime-active");

    // Hand the screen to a dedicated cinematic camera; the first-person one
    // (and its view-model child) stops rendering until halftime ends.
    const playerCam = this.player.getCameraEntity();
    if (playerCam.camera) playerCam.camera.enabled = false;

    const cam = new pc.Entity("halftime-camera");
    cam.addComponent("camera", { clearColor: SKY, fov: 50, nearClip: 0.05, farClip: 200 });
    this.app.root.addChild(cam);
    this.camera = cam;
    this.placeCamera(0);

    // Centred mannequin, facing the camera (model faces −Z at yaw 0). The
    // held weapon is hidden by playHalftime, so the id is just a formality.
    // rifle_walk_stop carries ~1.25 m of walk root motion — the camera's
    // hips-tracking pivot (see update) keeps the body centred through it,
    // so the spawn stays on the stage mark.
    const state: RemotePlayerState = {
      sessionId: "halftime-self",
      name: "halftime-self",
      x: 0,
      y: STAGE_Y,
      z: 0,
      yaw: 0,
      pitch: 0,
      health: 100,
      weaponId: team === "t" ? "ak47" : "m4a1_s",
      lastProcessedInputSeq: 0,
      team,
    };
    this.fighter = new RemotePlayer(
      this.app,
      "halftime-self",
      state,
      team === "ct" ? CT_ACCENT : T_ACCENT,
      this.factory,
    );
    this.fighter.playHalftime(pose);
  }

  /**
   * Slow orbital sweep (t: 0 → 1 over the phase): starts front-left and
   * above, arcs around to nearly head-on while pushing in and settling to
   * chest height — motion the whole 10s so the held pose never reads static.
   * Everything orbits the hips-tracking pivot, so the body stays centred
   * regardless of where a clip's root motion takes it.
   */
  private placeCamera(t: number): void {
    if (!this.camera) return;
    const e = t * t * (3 - 2 * t); // smoothstep — gentle in, gentle out
    const az = ((-42 + 50 * e) * Math.PI) / 180;
    // The settled walk-stop stand reads better with more air around it —
    // hold a wider final framing than the tighter stretch close-up.
    const rEnd = this.pose === "rifle_walk_stop" ? 4.4 : 3.3;
    const r = 4.7 + (rEnd - 4.7) * e;
    const f = this.focus;
    const y = f.y + 0.35 - 0.6 * e;
    this.camera.setPosition(f.x + Math.sin(az) * r, y, f.z - Math.cos(az) * r);
    this.camera.lookAt(f.x, f.y - 0.45, f.z);
  }

  private update(dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    this.fighter?.update(dt);
    // Trail the pivot onto the animated hips (soft follow — low-passes the
    // walk bob while keeping up with the root-motion drift).
    if (this.fighter?.getHipsWorld(this.hipsTmp)) {
      this.focus.lerp(this.focus, this.hipsTmp, Math.min(1, dt * 4));
    }
    this.placeCamera(Math.max(0, Math.min(1, this.elapsed / MATCH_FORMAT.halftimeSeconds)));
  }

  private end(): void {
    if (!this.active) return;
    this.active = false;
    this.overlay.hide();
    document.body.classList.remove("halftime-active");
    if (this.fighter) {
      this.fighter.destroy();
      this.fighter = null;
    }
    if (this.camera) {
      this.camera.destroy();
      this.camera = null;
    }
    const playerCam = this.player.getCameraEntity();
    if (playerCam.camera) playerCam.camera.enabled = true;
  }

  /** Standalone visual preview (`?halftime=1` CT / `?halftime=t` T, optional
   *  `&hfpose=stretch|rifle_walk_stop`) — loops the cinematic without a live
   *  match so framing / poses can be checked in one browser tab. The pose
   *  re-rolls each cycle when not forced. */
  debugStart(team: "ct" | "t", pose?: HalftimePose): void {
    this.debugLoop = true;
    const run = () => {
      this.begin({ team, pose });
      let s = MATCH_FORMAT.halftimeSeconds;
      this.overlay.setCountdown(s);
      const iv = window.setInterval(() => {
        s -= 1;
        this.overlay.setCountdown(Math.max(0, s));
        if (s <= 0) {
          window.clearInterval(iv);
          this.end();
          window.setTimeout(run, 1000);
        }
      }, 1000);
    };
    run();
  }
}
